import { cx } from 'linaria';
import * as React from 'react';
import { makeTestId, Typography } from '../';
import * as styles from './form-control.styles';

const formControlContext = React.createContext<FormControlContext>(null);

export interface FormControlProps {
    label?: string;
    annotation?: string;
    error?: string;
    characterLimit?: number;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    testId?: string;
}

export interface FormControlContext extends FormControlProps {
    setCharacterCount: (newCharacterCount: number) => void;
    characterCount: number;
    hasError: boolean;
    hasInputId: boolean;
    setInputId: (newId: string) => void;
}

export const FormControl: React.FC<React.PropsWithChildren<FormControlProps>> = (props) => {
    const { label, annotation, error, fullWidth, characterLimit, required, disabled, children, testId } = props;

    const hasError = Boolean(error);

    const [characterCount, setCharacterCount] = React.useState(0);
    const [inputId, setInputId] = React.useState<string>(null);

    const hasInputId = Boolean(inputId);

    const getAnnotationText = React.useCallback((): string => {
        if (hasError) {
            return error;
        }

        return annotation || null;
    }, [error, hasError, annotation]);

    return (
        <formControlContext.Provider
            value={{ ...props, characterCount, setCharacterCount, hasError, hasInputId, setInputId }}
        >
            <div className={cx(fullWidth && styles.fullWidth, disabled && styles.disabled)}>
                {label && (
                    <Typography
                        testId={makeTestId(testId, 'label')}
                        as="label"
                        variant="label-1"
                        color="body"
                        htmlFor={inputId}
                    >
                        {label} {required && '*'}
                    </Typography>
                )}

                <div className={styles.inputWrapper}>{children}</div>

                <div className={cx(styles.assistiveText)}>
                    <Typography
                        testId={makeTestId(testId, 'error')}
                        variant="caption-1"
                        color={hasError ? 'alert' : disabled ? 'secondary' : 'body'}
                    >
                        {getAnnotationText()}
                    </Typography>

                    {characterLimit && (
                        <Typography
                            testId={makeTestId(testId, 'count')}
                            variant="caption-1"
                        >{`${characterCount}/${characterLimit}`}</Typography>
                    )}
                </div>
            </div>
        </formControlContext.Provider>
    );
};

export const useFormControl = (): FormControlContext => React.useContext(formControlContext);
